/* eslint-disable no-useless-escape */
import type { LocalStorageDataT } from '@/constant/localStorageData'
import { persistentLocalStorageKeys } from '@/constant/localStorageData'
import type { SessionStorageDataT } from '@/constant/sessionStorageData'

type LocalStorageKeyT = keyof LocalStorageDataT & string
type SessionStorageKeyT = keyof SessionStorageDataT & string
type StorageValueT<K extends LocalStorageKeyT | SessionStorageKeyT> = K extends LocalStorageKeyT
  ? LocalStorageDataT[K]
  : K extends SessionStorageKeyT
    ? SessionStorageDataT[K]
    : never

type StorageItemT<K extends LocalStorageKeyT | SessionStorageKeyT> = {
  key: K
  value: StorageValueT<K>
}

const LOCAL_STORAGE_VERSION = 3
const SESSION_STORAGE_VERSION = 2

type DoubleWrappedRankOptions = { options: LocalStorageDataT['REPORT_RANK_OPTIONS'] }

// 과거 마이그레이션이 신 형태를 한 번 더 감싸 options 자리에 { options, hasRank } 가 들어간 값.
// 원래 값이 안쪽에 남아 있어 되살릴 수 있다.
const isDoubleWrappedRankOptions = (value: unknown): value is DoubleWrappedRankOptions => {
  const inner = (value as DoubleWrappedRankOptions | null)?.options

  return !!inner && !Array.isArray(inner) && Array.isArray(inner.options)
}

// @see 문서: https://www.notion.so/freewheelin/bda97550def44bcbb26e4df9a3d3bcb5
// 버전 업데이트할 때마다 문서 업데이트 부탁드립니다.
export class StorageService<
  K extends LocalStorageKeyT | SessionStorageKeyT = LocalStorageKeyT | SessionStorageKeyT,
> {
  private storage: Storage
  type: 'local' | 'session'
  targetVersion: LocalStorageDataT['VERSION']
  currentVersion: LocalStorageDataT['VERSION']

  constructor({ type }: { type: K extends LocalStorageKeyT ? 'local' : 'session' }) {
    this.type = type
    this.storage = type === 'local' ? localStorage : sessionStorage
    this.currentVersion = this.getItem('VERSION') ?? 1
    this.targetVersion = type === 'local' ? LOCAL_STORAGE_VERSION : SESSION_STORAGE_VERSION
  }

  setItem<K extends LocalStorageKeyT | SessionStorageKeyT>(k: K) {
    return (v: StorageValueT<K>) =>
      this.storage.setItem(k, typeof v === 'string' ? v : JSON.stringify(v))
  }

  setItems(items: StorageItemT<K>[]) {
    items.forEach(({ key, value }) => this.setItem(key)(value))
  }

  getItem<K extends LocalStorageKeyT | SessionStorageKeyT>(k: K): StorageValueT<K> | null {
    const item = this.storage.getItem(k)
    try {
      // TODO: v1 코드
      if (item === 'TRUE' || item === 'FALSE') {
        this.setItem(k as LocalStorageKeyT)(item === 'TRUE')
        return (item === 'TRUE') as StorageValueT<K>
      }

      // TODO: v1 코드
      if (item === JSON.stringify('TRUE') || item === JSON.stringify('FALSE')) {
        this.setItem(k as LocalStorageKeyT)(item === JSON.stringify('TRUE'))
        return (item === JSON.stringify('TRUE')) as StorageValueT<K>
      }

      if (k === 'SAVED_LOGIN_ID') {
        return item as StorageValueT<K> | null
      }

      return item ? JSON.parse(item) : null
    } catch (_) {
      return item as StorageValueT<K>
    }
  }

  removeItem<K extends LocalStorageKeyT | SessionStorageKeyT>(k: K) {
    this.storage.removeItem(k)
  }

  // unRegisterPersistentItems(keyArr: K[]) {
  //   this.keyOfPersistentItems = this.keyOfPersistentItems.filter((pk) => !keyArr.includes(pk))
  // }

  clear() {
    this.storage.clear()
  }

  clearExceptPermanent() {
    const permanentItems = Object.keys(this.storage)
      .filter((key) => {
        // '팝업만료_T1234'에서 '팝업만료'만 있어도 남기기 위해 string include를 체크
        return persistentLocalStorageKeys.some((pk) => key.includes(pk)) && !!this.getItem(key as K)
      })
      .map((key) => ({
        key,
        value: this.getItem(key as K),
      }))

    this.clear()
    this.setItems(permanentItems as StorageItemT<K>[])
  }

  async initializeStorage() {
    return new Promise((resolve, reject) => {
      try {
        // 버저닝 이전 데이터 migration
        const currentVersion = this.currentVersion
        const targetVersion = this.targetVersion
        if (currentVersion < targetVersion) {
          this.migrateToLatest()
        }

        // set storage version to latest
        this.setItem('VERSION')(targetVersion)

        resolve(true)
      } catch (e) {
        console.error(e)
        reject(e.message)
      }
    })
  }

  migrateToLatest() {
    // 모든 하위 버전에서 타야하는 로직
    {
      if (this.type === 'local') {
        const rankOptions = this.getItem('REPORT_RANK_OPTIONS')

        // 이 블록은 버전 가드 밖이라 VERSION 을 올릴 때마다 다시 실행된다. 형태를 보지 않고
        // 감싸면 이미 신 형태인 값이 { options: { options: [...] } } 로 이중 래핑되어
        // 저장된 옵션과 hasRank 가 통째로 유실되므로, 구 형태일 때만 감싼다.
        if (Array.isArray(rankOptions)) {
          this.setItem('REPORT_RANK_OPTIONS')({ options: rankOptions, hasRank: false })
        } else if (isDoubleWrappedRankOptions(rankOptions)) {
          this.setItem('REPORT_RANK_OPTIONS')(rankOptions.options)
        }
      }
    }

    // v1 changelog - v1일 경우 반드시 타야하는 로직
    if (this.currentVersion === 1) {
      // localStorage, sessionStorage 공통
      // 1. v1의 stringified string "와 \벗겨낸다.
      const possibleStringfiedKeys = [
        'SAVED_LOGIN_ID',
        'x-auth-token',
        'print-api',
        'teacher-id',
        'TEACHER_ID',
        'X_AUTH_TOKEN',
        'PRINT_API',
        'ASSIGN_TYPE',
        'DEV_API',
        'LOGIN_PATH',
        'SMS_RECIPIENT',
        'SMS_TYPE',
        'TODAY_FIRST_USE_HISTORY',
        'RECENT_PRINT_NAME',
        '수업_아코디언_학생타입',
        '출결_아코디언_학생타입',
      ]

      Object.entries(this.storage).forEach(([key, value]) => {
        if (possibleStringfiedKeys.includes(key)) {
          this.setItem(key as K)(value.replace(/\"|\\/g, ''))
        }
      })

      // localStorage only
      if (this.type === 'local') {
        // 2. 1번 후, v1의 ""로 감싸지지 않은 Date string을 timestamp로 변환한다.
        const dateStr = this.getItem('TODAY_FIRST_USE_HISTORY')
        if (dateStr) {
          const date = new Date(dateStr).getTime()
          this.setItem('TODAY_FIRST_USE_HISTORY')(date)
        }

        // 3. v1의 다음 값들의 현재 저장된 옵션을 true로 고정한다.
        const chapterOption = this.getItem('BY_CHAPTER_OPTION')
        if (chapterOption) {
          chapterOption.onlyAutoScorable = true
          this.setItem('BY_CHAPTER_OPTION')(chapterOption)
        }

        const schoolTestOption = this.getItem('BY_SCHOOL_TEST_OPTION')
        if (schoolTestOption) {
          this.setItem('BY_SCHOOL_TEST_OPTION')(schoolTestOption)
        }

        const practiceOption = this.getItem('BY_PRACTICE_OPTION')
        if (practiceOption) {
          practiceOption.onlyAutoScorable = true
          this.setItem('BY_PRACTICE_OPTION')(practiceOption)
        }

        // const workbookOption = this.getItem('BY_WORKBOOK_DEFAULT_OPTION')
        // if (workbookOption) {
        //   workbookOption.onlyAutoScorable = true
        //   this.setItem('BY_WORKBOOK_DEFAULT_OPTION')(workbookOption)
        // }

        // const workbookTwinOption = this.getItem('BY_WORKBOOK_TWIN_OPTION')
        // if (workbookTwinOption) {
        //   workbookTwinOption.onlyAutoScorable = true
        //   this.setItem('BY_WORKBOOK_TWIN_OPTION')(workbookTwinOption)
        // }
      }
    }
  }

  // TODO: 공통화하다가 시간없어서..
  // static mapUtil<
  //   T extends LocalStorageKeyT | SessionStorageKeyT,
  //   U = T extends LocalStorageKeyT
  //     ? LocalStorageDataT[T]
  //     : T extends SessionStorageKeyT
  //       ? SessionStorageDataT[T]
  //       : never,
  //   V = U extends object ? U : never,
  //   K = U extends object ? (keyof U extends T ? keyof U : never) :never,
  // >(key: K) {
  //   return {
  //     getMap: (storageService: StorageService<K>) => {
  //       const storageData = storageService.getItem(key)
  //       return storageData ? new Map<K, V>(Object.entries(storageData)) : new Map<K, U>()
  //     },
  //     setMap: (storageService: StorageService) => (map: Map<K, U>) => {
  //       if (map.size === 0) {
  //         storageService.removeItem(key)
  //       } else {
  //         storageService.setItem(key)(Object.fromEntries(map))
  //       }
  //     },
  //     getItem: (storageService: StorageService) => (value) => {
  //       const map = this.mapUtil(key).getMap(key)(storageService)
  //       return map.get(value)
  //     },
  //     setItem: (storageService: StorageService) => (value) => {
  //       const map = this.getMap(key)(storageService)
  //       map.set(key, value)
  //       this.map.setMap(storageService)(map)
  //     },
  //   }
  // }
}

export const localStorageService = new StorageService<LocalStorageKeyT>({
  type: 'local',
})

export const sessionStorageService = new StorageService<SessionStorageKeyT>({ type: 'session' })

// 사파리 프라이빗 브라우징 등에서는 storage 접근 자체가 예외로 실패한다.
// 셸 정보를 읽거나 쓰지 못해도 앱은 떠야 하므로 실패를 삼킨다.
export const readAppDeviceInfo = () => {
  try {
    return sessionStorageService.getItem('APP_DEVICE_INFO')
  } catch {
    return null
  }
}

export const writeAppDeviceInfo = (appDeviceInfo: SessionStorageDataT['APP_DEVICE_INFO']) => {
  try {
    sessionStorageService.setItem('APP_DEVICE_INFO')(appDeviceInfo)
  } catch {
    return
  }
}

// storage 접근이 막힌 환경에서 초기화가 실패하더라도, 호출부의 로그인 리다이렉트까지
// 함께 끊기지 않도록 각 저장소를 독립적으로 비운다.
const clearStorageSafely = (getStorage: () => Storage) => {
  try {
    getStorage().clear()
  } catch {
    return
  }
}

/**
 * 인증 상태가 무효해져 로그인 화면으로 되돌릴 때의 storage 초기화.
 *
 * 안드로이드 셸은 device/version 을 웹뷰 진입 URL 로 한 번만 넘긴다. 그 값까지 함께 비우면
 * SPA 라우팅으로 쿼리가 사라진 뒤에는 되찾을 방법이 없어, 앱을 재실행하기 전까지 학습지·교재
 * 다운로드가 막힌다. 웹뷰 세션에 묶인 값이라 로그아웃과 함께 버릴 이유도 없으므로 되돌려 둔다.
 */
// 부팅 경로(authDaemon)와 렌더 경로에서 함께 쓰는 쓰기. 쿼터 초과·저장소 차단으로 setItem 이
// 던지면 부팅이 거절되어 앱이 아예 렌더되지 않으므로, 기록 실패를 삼키고 진행한다.
export const writeLocalItemSafely = <K extends LocalStorageKeyT>(
  key: K,
  value: StorageValueT<K>,
) => {
  try {
    localStorageService.setItem(key)(value)
  } catch {
    return
  }
}

export const clearStorageForSignOut = () => {
  const appDeviceInfo = readAppDeviceInfo()

  clearStorageSafely(() => sessionStorage)
  clearStorageSafely(() => localStorage)

  if (appDeviceInfo) writeAppDeviceInfo(appDeviceInfo)
}

/**
 * 학원을 옮길 때의 세션 저장소 초기화.
 *
 * 세션 저장소에는 「수업 그룹별 마지막 경로」처럼 이전 학원의 반·학생 id 를 값에 담은 항목이
 * 있는데, 학원 전환은 같은 탭에서의 재진입이라 이 값들이 그대로 살아남는다. 두면 전환 직후
 * 수업 탭이 이전 학원 경로로 이동해 다른 학원 데이터를 요청하게 된다.
 *
 * 로컬 저장소는 비우지 않는다. 학원에 묶인 값은 전환 응답으로 덮어쓰고, 나머지는 기기·계정
 * 단위 설정이라 학원이 바뀌었다고 버릴 이유가 없다.
 *
 * 로그인 상태 유지를 끈 세션은 토큰도 여기에 있으므로, 반드시 새 토큰을 쓰기 전에 호출한다.
 */
export const clearSessionStorageForAcademySwitch = () => {
  const appDeviceInfo = readAppDeviceInfo()

  clearStorageSafely(() => sessionStorage)

  if (appDeviceInfo) writeAppDeviceInfo(appDeviceInfo)
}
